import { getExternalUserLicenses, getStateLicenseRequirement, fetchCompanyLicenseOnly, fetchUserLicenseOnly } from '../services/userApi';
import { getOrgConfig } from './organizationConfigUtil';

// Client Approval contact link, resolved once at module load
const clientApprovalEmail = getOrgConfig()?.emails?.clientApproval;
const clientApprovalLink = `<a href="mailto:${clientApprovalEmail}">${clientApprovalEmail}</a>`;

export const runLicensingGateForPage = async (loanType, stateCode, tpoOrgId, tpoUserId, city, legalEntityType) => {
    if (!stateCode || !stateCode.trim()) {
        return {
            blocked: true,
            companyRequired: false,
            loRequired: false,
            companyHas: false,
            loHas: false,
            stateCode: "",
            stateName: "",
            reason: "State is required to validate licensing.",
            message: "State is required to validate licensing."
        };
    }

    try {
        const authObject = await window.elli.script.getObject('auth');
        const freshAuthCode = await authObject.createAuthCode();
        // API still takes stateCode; do NOT evaluate city from license response (city not valid anymore)
        const lic = await getExternalUserLicenses(freshAuthCode, tpoOrgId, tpoUserId, stateCode);

        if (!lic || lic.error) {
            return {
                blocked: true,
                companyRequired: false,
                loRequired: false,
                companyHas: false,
                loHas: false,
                stateCode,
                stateName: '',
                reason: 'Licensing service unavailable. Unable to verify licenses.'
            };
        }

        if (skipStateLicenseValidation(lic.companyLenderType)) {
            return {
                blocked: false,
                companyRequired: false,
                loRequired: false,
                companyHas: false,
                loHas: false,
                stateCode: '',
                city: '',
                stateName: '',
                reason: '',
                message: '',
                shortMessage: '',
                brokerSelection: lic.brokerSelection || ""
            }
        }

        return evaluateLicensing(loanType, lic, stateCode, city, legalEntityType);
    } catch {
        return {
            blocked: true,
            companyRequired: false,
            loRequired: false,
            companyHas: false,
            loHas: false,
            stateCode,
            stateName: '',
            reason: 'Licensing check failed unexpectedly. Please try again.'
        };
    }
};

const evaluateLicensing = (loanType, lic, stateCode, city, legalEntityType) => {
    const stateReqRaw = lic?.stateRequirement;
    const stateReqList = Array.isArray(stateReqRaw) ? stateReqRaw : (stateReqRaw ? [stateReqRaw] : []);

    const normalize = (v) => (v || '').toString().trim().toLowerCase();
    const st = (stateCode || '').toString().trim().toUpperCase();

    const ltRaw = (loanType || '').trim().toLowerCase(); // "consumer" | "business"
    const ltKey = ltRaw === 'business' ? 'business' : 'consumer';

    const sr =
        stateReqList.find(r =>
            ((r.stateCode || '').toString().trim().toUpperCase() === st) &&
            normalize(r.loanType) && normalize(r.loanType) === ltKey
        )
        || stateReqList.find(r =>
            ((r.stateCode || '').toString().trim().toUpperCase() === st) &&
            !normalize(r.loanType)
        )
        || {};

    const brokerReq = (sr.brokerLicenseRequired || '').trim();
    const loReq = (sr.loanOriginatorLicenseRequired || '').trim();

    const stateName = sr.state || "this state";

    const companyHas = !!(lic?.companyLicense?.stateAbbreviation);
    const loHas = !!(lic?.userLicense?.stateAbbreviation);

    let blocked = false;
    let message = "";
    let shortMessage = "";

    // required flags for UI + "No Issues"
    let companyRequired = false;
    let loRequired = false;

    const stUpper = (stateCode || '').toString().trim().toUpperCase();

    const le = (legalEntityType || '').toString().trim().toLowerCase();
    const isCorpOrLlc =
        le === 'corporation' ||
        le === 'llc';

    const isConsumer = ((loanType || '').trim().toLowerCase() === "consumer");
    const isBusiness = !isConsumer;

    const allowMissingLicensesForVAEntity = isBusiness && stUpper === 'VA' && isCorpOrLlc;

    // --- Consumer: ignore brokerReq/loReq flags completely ---
    if (isConsumer) {
        companyRequired = true;
        loRequired = true;

        if (!companyHas || !loHas) {
            blocked = true;

            if (!companyHas) {
                message = `${stateName} state requires a Broker License. The selected company does not have a valid license. Please contact Client Approval at ${clientApprovalLink}.`;
                shortMessage = `${stateName} state requires a Broker License. Click for details.`;
            } else if (!loHas) {
                message = `${stateName} state requires a Loan Originator License. The selected loan officer does not have a valid license. Please contact Client Approval at ${clientApprovalLink}.`;
                shortMessage = `${stateName} state requires a Loan Originator License. Click for details.`;
            }
        }
    } else {
        // --- Business rules ---
        companyRequired = (brokerReq === "Yes");
        loRequired = (loReq === "Yes");

        if (allowMissingLicensesForVAEntity) {
            companyRequired = false;
            loRequired = false;
        }

        const normalizedCity = (city || '').toString().trim().toLowerCase();
        const restrictedCities = Array.isArray(sr.restrictedCities) ? sr.restrictedCities : [];
        const hasRestrictedCities = restrictedCities.length > 0;

        const isRestrictedCity =
            !!normalizedCity &&
            restrictedCities.some(rc => (rc || '').toString().trim().toLowerCase() === normalizedCity);

        // 1) city-based restriction takes priority when configured
        if (hasRestrictedCities) {
            if (isRestrictedCity) {
                blocked = true;

                if ((sr.loanType || '').trim() === "Business") {
                    message =
                        `This loan is not eligible for investment transactions in ${city}, ${stateName}. ` +
                        `You cannot submit it for this location. ` +
                        `Please contact Client Approval at ${clientApprovalLink}.`;
                    shortMessage = `Not eligible for investment transactions in ${city}, ${stateName}. Click for details.`;
                } else if ((sr.loanType || '').trim() === "Consumer") {
                    message =
                        `This loan is not eligible for Consumer transactions in ${city}, ${stateName}. ` +
                        `You cannot submit it for this location. ` +
                        `Please contact Client Approval at ${clientApprovalLink}.`;
                    shortMessage = `Not eligible for Consumer transactions in ${city}, ${stateName}. Click for details.`;
                } else {
                    message =
                        `This loan is not eligible for transactions in ${city}, ${stateName}. ` +
                        `You cannot submit it for this location. ` +
                        `Please contact Client Approval at ${clientApprovalLink}.`;
                    shortMessage = `Not eligible for transactions in ${city}, ${stateName}. Click for details.`;
                }
            }
        } else {
            // 2) legacy full-state restriction logic
            if (brokerReq === "" || loReq === "") {
                blocked = true;

                if ((sr.loanType || '').trim() === "Business") {
                    message =
                        `This loan is not eligible for investment transactions in ${city}, ${stateName}. ` +
                        `You cannot submit it for this location. ` +
                        `Please contact Client Approval at ${clientApprovalLink}.`;
                    shortMessage = `Not eligible for investment transactions in ${city}, ${stateName}. Click for details.`;
                } else if ((sr.loanType || '').trim() === "Consumer") {
                    message =
                        `This loan is not eligible for Consumer transactions in ${city}, ${stateName}. ` +
                        `You cannot submit it for this location. ` +
                        `Please contact Client Approval at ${clientApprovalLink}.`;
                    shortMessage = `Not eligible for Consumer transactions in ${city}, ${stateName}. Click for details.`;
                } else {
                    message =
                        `This loan is not eligible for transactions in ${city}, ${stateName}. ` +
                        `You cannot submit it for this location. ` +
                        `Please contact Client Approval at ${clientApprovalLink}.`;
                    shortMessage = `Not eligible for transactions in ${city}, ${stateName}. Click for details.`;
                }
            }
        }

        // Broker license check ONLY if required
        if (!blocked && companyRequired && !companyHas) {
            if (!allowMissingLicensesForVAEntity) {
                blocked = true;
                message = `${stateName} state requires a Broker License. The selected company does not have a valid license. Please contact Client Approval at ${clientApprovalLink}.`;
                shortMessage = `${stateName} state requires a Broker License. Click for details.`;
            }
        }

        // LO license check ONLY if required (THIS fixes your case: loReq="No" won't block or show missing)
        if (!blocked && loRequired && !loHas) {
            if (!allowMissingLicensesForVAEntity) {
                blocked = true;
                message = `${stateName} state requires a Loan Originator License. The selected loan officer does not have a valid license. Please contact Client Approval at ${clientApprovalLink}.`;
                shortMessage = `${stateName} state requires a Loan Originator License. Click for details.`;
            }
        }
    }

    return {
        blocked,
        companyRequired,
        loRequired,
        companyHas,
        loHas,
        stateCode: sr.stateCode || st || "",
        stateName,
        message,
        shortMessage,
        brokerSelection: lic.brokerSelection || ""
    };
};



/**
 * Returns array of HTML-safe message strings (no mailto) describing soft warnings.
 * Will be empty if no warnings OR if effective state is empty.
 *
 * @param {string} loanTypeValue
 * @param {object|Array|null} stateLic
 * @param {object} companyData
 * @param {object} loanOfficerData
 * @param {string} propertyState
 * @param {string} propertyCity
 */
export const collectSoftLicenseWarnings = (
    loanTypeValue,
    stateLic,
    companyData,
    loanOfficerData,
    propertyState = '',
    propertyCity = '',
    loLicenseOverride = null
) => {
    const warnings = [];

    // effective state / city
    const effectiveState =
        (propertyState || '').trim() ||
        companyData?.address?.state ||
        '';

    const effectiveCity =
        (propertyCity || '').trim() ||
        companyData?.address?.city ||
        '';

    // If we don't have a state to evaluate, do not collect warnings
    if (!effectiveState) {
        return warnings; // empty -> no toast
    }

    const companyHasLicense = !!(companyData?.license?.stateAbbreviation || companyData?.license?.licenseNumber);
    const effectiveLoLicense = loLicenseOverride || null;
    const loHasLicense = !!(effectiveLoLicense?.stateAbbreviation || effectiveLoLicense?.licenseNumber);

    // Normalize
    const lt = (loanTypeValue || '').toLowerCase();

    // stateLic can be array now
    const stateRules = Array.isArray(stateLic) ? stateLic : (stateLic ? [stateLic] : []);

    // pick matching rule by state + loanType (most specific first); do NOT use city from state license response
    let sr = null;

    if (stateRules.length > 0) {
        // 1) state + loanType
        sr = stateRules.find(r =>
            ((r.stateCode || '').trim().toUpperCase() === effectiveState.trim().toUpperCase()) &&
            ((r.loanType || '').trim() !== '' && (r.loanType || '').trim().toLowerCase() === (lt === 'business' ? 'business' : 'consumer'))
        )
            // 2) state only (loanType wildcard)
            || stateRules.find(r =>
                ((r.stateCode || '').trim().toUpperCase() === effectiveState.trim().toUpperCase()) &&
                ((r.loanType || '').trim() === '')
            );
    }

    const stateName = sr?.state || effectiveState || 'this state';

    // restrictedCities overlay (does not change existing rule selection logic)
    const normalizedCity = effectiveCity.trim().toLowerCase();
    const restrictedCities = Array.isArray(sr?.restrictedCities) ? sr.restrictedCities : [];
    const isRestrictedCity =
        !!normalizedCity &&
        restrictedCities.some(city => (city || '').trim().toLowerCase() === normalizedCity);

    if (isRestrictedCity) {
        if ((sr?.loanType || '').trim() === "Business") {
            warnings.push(`This loan is not eligible for investment transactions in ${effectiveCity}, ${stateName}. Please contact Client Approval for assistance.`);
        } else if ((sr?.loanType || '').trim() === "Consumer") {
            warnings.push(`This loan is not eligible for Consumer transactions in ${effectiveCity}, ${stateName}. Please contact Client Approval for assistance.`);
        } else {
            warnings.push(`This loan is not eligible for transactions in ${effectiveCity}, ${stateName}. Please contact Client Approval for assistance.`);
        }

        return warnings;
    }

    if (lt === 'business') {
        // If we have state-level licensing rules, use them
        if (sr) {
            const brokerReq = (sr.brokerLicenseRequired || '').trim();
            const loReq = (sr.loanOriginatorLicenseRequired || '').trim();

            // Broker checks
            if (brokerReq === 'Yes' && !companyHasLicense) {
                warnings.push(`${stateName} requires a Broker License. The selected company does not have a valid license.`);
            } else if (brokerReq === '') {
                if ((sr.loanType || '').trim() === "Business") {
                    warnings.push(`This loan is not eligible for investment transactions in ${effectiveCity}, ${stateName}. Please contact Client Approval for assistance.`);
                } else if ((sr.loanType || '').trim() === "Consumer") {
                    warnings.push(`This loan is not eligible for Consumer transactions in ${effectiveCity}, ${stateName}. Please contact Client Approval for assistance.`);
                } else {
                    warnings.push(`This loan is not eligible for transactions in ${effectiveCity}, ${stateName}. Please contact Client Approval for assistance.`);
                }
            }

            // LO checks (only if broker didn't already trigger restriction)
            if (brokerReq !== '') {
                if (loReq === 'Yes' && !loHasLicense) {
                    warnings.push(`${stateName} requires a Loan Originator License. The selected loan officer does not have a valid license.`);
                } else if (loReq === '') {
                    if ((sr.loanType || '').trim() === "Business") {
                        warnings.push(`This loan is not eligible for investment transactions in ${effectiveCity}, ${stateName}. Please contact Client Approval for assistance.`);
                    } else if ((sr.loanType || '').trim() === "Consumer") {
                        warnings.push(`This loan is not eligible for Consumer transactions in ${effectiveCity}, ${stateName}. Please contact Client Approval for assistance.`);
                    } else {
                        warnings.push(`This loan is not eligible for transactions in ${effectiveCity}, ${stateName}. Please contact Client Approval for assistance.`);
                    }
                }
            }
        } else {
            // No state rule object — fall back to checking the selected entities
            if (!companyHasLicense) {
                warnings.push(`Selected company does not have a valid license record for ${stateName}. Please contact Client Approval for assistance.`);
            }
            if (!loHasLicense) {
                warnings.push(`Selected loan officer does not have a valid license record for ${stateName}. Please contact Client Approval for assistance.`);
            }
        }
    } else {
        // Consumer flow: ignore stateLicenses; simply check selected company & LO license exist
        if (!companyHasLicense) {
            warnings.push(`Selected company does not have a company license. Please contact Client Approval for assistance.`);
        }
        if (!loHasLicense) {
            warnings.push(`Selected loan officer does not have a license. Please contact Client Approval for assistance.`);
        }
    }

    return warnings;
};

/**
 * Evaluates licensing rules for Send Disclosures / Disclosures.
 * Returns true if licensing gate should force validation.
 */
export const runLicensingGateForMenu = async (
    loanType,
    stateCode,
    city,
    tpoOrgId,
    tpoUserId,
    accessToken,
    legalEntityType
) => {
    // If no state or org id, no license gate
    if (!stateCode || (!tpoOrgId && tpoOrgId !== "0")) return false;

    // If accessToken not provided, conservative – force validation
    if (!accessToken) {
        console.warn("runLicensingGate: no accessToken provided. Forcing validation.");
        return true;
    }
    // 1) State requirement (can be object OR array now)
    let stateRequirementRaw = null;
    try {
        stateRequirementRaw = await getStateLicenseRequirement(stateCode);
    } catch (err) {
        console.error("State requirement call failed:", err);
        return true; // conservative
    }

    // Normalize to array
    const stateRules = Array.isArray(stateRequirementRaw)
        ? stateRequirementRaw
        : (stateRequirementRaw ? [stateRequirementRaw] : []);

    // Pick best-matching rule by state + loanType (most specific first)
    const effectiveState = (stateCode || "").trim().toUpperCase();
    const lt = (loanType || "").trim().toLowerCase(); // "business" / "consumer"

    let stateRequirement = null;

    if (stateRules.length > 0) {
        // 1) state + loanType
        stateRequirement =
            stateRules.find(r =>
                ((r.stateCode || "").trim().toUpperCase() === effectiveState) &&
                ((r.loanType || "").trim() !== "" && (r.loanType || "").trim().toLowerCase() === lt)
            )
            // 2) state only (loanType wildcard)
            || stateRules.find(r =>
                ((r.stateCode || "").trim().toUpperCase() === effectiveState) &&
                ((r.loanType || "").trim() === "")
            );
    }

    // If we couldn't find any applicable rule, be conservative
    if (!stateRequirement) {
        console.warn("No matching stateRequirement rule found. Forcing validation.");
        return true;
    }

    // --- VA Business Corp/LLC exception flag (allow missing licenses) ---
    const le = (legalEntityType || "").toString().trim().toLowerCase();
    const isCorpOrLlc =
        le === "corporation" ||
        le === "llc";

    const isConsumer = ((loanType || "").trim().toLowerCase() === "consumer");
    const isBusiness = !isConsumer;

    const allowMissingLicensesForVAEntity =
        isBusiness && effectiveState === "VA" && isCorpOrLlc;

    // 2) Company license
    let companyLicense = null;
    let companyLenderType = null;

    try {
        const comp = await fetchCompanyLicenseOnly({ accessToken, tpoOrgId, stateCode });
        if (comp?.error) console.error("Company license lookup returned error");
        companyLicense = comp?.licenseDetails || null;
        companyLenderType = comp?.lenderType || null;
    } catch (err) {
        console.error("Company license lookup failed:", err);
    }

    if (skipStateLicenseValidation(companyLenderType)) return false;

    // 3) User license
    let userLicense = null;
    try {
        const u = await fetchUserLicenseOnly({ accessToken, tpoUserId, stateCode });
        if (u?.error) console.error("User license lookup returned error");
        userLicense = u;
    } catch (err) {
        console.error("User license lookup failed:", err);
    }

    const hasLicense = (obj) => !!obj;

    if (isConsumer) {
        // Consumer ignores broker/LO flags: missing company or user license => block
        if (!hasLicense(companyLicense) || !hasLicense(userLicense)) return true;
    } else {
        const brokerReq = (stateRequirement.brokerLicenseRequired || "").trim();
        const loReq = (stateRequirement.loanOriginatorLicenseRequired || "").trim();

        const normalizedCity = (city || "").trim().toLowerCase();
        const restrictedCities = Array.isArray(stateRequirement.restrictedCities)
            ? stateRequirement.restrictedCities
            : [];

        const hasRestrictedCities = restrictedCities.length > 0;

        const isRestrictedCity =
            !!normalizedCity &&
            restrictedCities.some(rc => (rc || "").trim().toLowerCase() === normalizedCity);

        // 1) city-based restriction takes priority when configured
        if (hasRestrictedCities) {
            if (isRestrictedCity) return true;
        } else {
            // 2) legacy full-state restriction logic
            if (brokerReq === "") return true;
            if (loReq === "") return true;
        }

        // Broker required => block only if missing AND not VA Corp/LLC exception
        if (brokerReq === "Yes") {
            if (!hasLicense(companyLicense) && !allowMissingLicensesForVAEntity) return true;
        }

        // LO required => block only if missing AND not VA Corp/LLC exception
        if (loReq === "Yes") {
            if (!hasLicense(userLicense) && !allowMissingLicensesForVAEntity) return true;
        }
    }

    return false;
};

export const skipStateLicenseValidation = (lenderType) => {
    if (lenderType === 'StateLicensedLenderNonDepositoryInstitution' || lenderType === 'None') {
        return false;
    }
    return true;
}